import React, { createContext, useState, useContext, useEffect } from 'react';
import { initialUsers, defaultAboutInfoTemplate } from './mockData';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  // 初始化用户数据到localStorage（如果没有的话）
  useEffect(() => {
    const savedUsers = localStorage.getItem('blog_users');
    if (!savedUsers) {
      localStorage.setItem('blog_users', JSON.stringify(initialUsers));
    }
  }, []);

  // 从localStorage加载用户数据，如果没有则使用默认数据
  const loadUserFromStorage = () => {
    const currentUser = localStorage.getItem('blog_current_user');
    if (currentUser) {
      const user = JSON.parse(currentUser);
      return {
        ...defaultAboutInfoTemplate,
        ...user,
        name: user.name || user.username,
        email: user.email || '未设置邮箱',
        // 确保 skills 是数组
        skills: Array.isArray(user.skills) ? user.skills : 
                typeof user.skills === 'string' ? [user.skills] : 
                []
      };
    }
    
    // 默认用户数据（未登录状态）
    return {
      name: 'zxj',
      email: 'zxj@yand.com',
      position: '智克',
      interests: '智克',
      bio: '这个人很神秘，什么也没留下。',
      location: '中国',
      joinDate: '2023年1月',
      socialMedia: '微博个人信息',
      skills: ['React', 'TypeScript', 'Node.js', 'MongoDB', 'Docker', 'Git'],
      techStack: [
        { name: 'React', level: 85 },
        { name: 'TypeScript', level: 75 },
        { name: 'Node.js', level: 80 },
        { name: 'MongoDB', level: 70 },
        { name: 'Docker', level: 65 },
        { name: 'Git', level: 90 }
      ],
      avatar: '', // 头像URL或base64
      avatarColor: '#4a90e2', // 默认头像颜色
      isLoggedIn: false
    };
  };

  const [user, setUser] = useState(loadUserFromStorage());
  const [isEditing, setIsEditing] = useState(false);

  // 当用户数据变化时保存到localStorage
  useEffect(() => {
    const currentUser = localStorage.getItem('blog_current_user');
    if (currentUser) {
      const userObj = JSON.parse(currentUser);
      const updatedUser = { ...userObj, ...user };
      localStorage.setItem('blog_current_user', JSON.stringify(updatedUser));
    }
  }, [user]);

  // 更新用户信息
  const updateUser = (updatedData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedData
    }));
  };

  // 更新技能栈
  const updateTechStack = (techStack) => {
    setUser(prevUser => ({
      ...prevUser,
      techStack
    }));
  };

  // 添加技能
  const addSkill = (skill) => {
    if (user.skills.includes(skill)) {
      return;
    }
    setUser(prevUser => ({
      ...prevUser,
      skills: [...prevUser.skills, skill]
    }));
  };

  // 删除技能
  const removeSkill = (skill) => {
    setUser(prevUser => ({
      ...prevUser,
      skills: prevUser.skills.filter(s => s !== skill)
    }));
  };

  // 更新头像
  const updateAvatar = (avatarData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...avatarData
    }));
  };

  // 重置为默认数据
  const resetToDefault = () => {
    const defaultUser = {
      name: 'zxj',
      email: 'zxj@yand.com',
      position: '智克',
      interests: '智克',
      bio: '这个人很神秘，什么也没留下。',
      location: '中国',
      joinDate: '2023年1月',
      socialMedia: '微博个人信息',
      skills: ['React', 'TypeScript', 'Node.js', 'MongoDB', 'Docker', 'Git'],
      techStack: [
        { name: 'React', level: 85 },
        { name: 'TypeScript', level: 75 },
        { name: 'Node.js', level: 80 },
        { name: 'MongoDB', level: 70 },
        { name: 'Docker', level: 65 },
        { name: 'Git', level: 90 }
      ],
      avatar: '',
      avatarColor: '#4a90e2'
    };
    setUser(defaultUser);
    localStorage.setItem('blog_user', JSON.stringify(defaultUser));
  };

  // 登录用户
  const loginUser = (userData) => {
    const userWithLoginState = {
      ...userData,
      isLoggedIn: true
    };
    setUser(userWithLoginState);
    localStorage.setItem('blog_current_user', JSON.stringify(userWithLoginState));
  };

  // 注销用户
  const logoutUser = () => {
    const defaultUser = loadUserFromStorage();
    setUser({
      ...defaultUser,
      isLoggedIn: false
    });
    localStorage.removeItem('blog_current_user');
  };

  // 检查是否已登录
  const isLoggedIn = () => {
    return !!localStorage.getItem('blog_current_user');
  };

  return (
    <UserContext.Provider value={{
      user,
      isEditing,
      setIsEditing,
      updateUser,
      updateTechStack,
      addSkill,
      removeSkill,
      updateAvatar,
      resetToDefault,
      loginUser,
      logoutUser,
      isLoggedIn
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};